// https://web-crunch.com/posts/rails-drag-drop-active-storage-stimulus-dropzone

import Dropzone from "dropzone";
import { Controller } from "stimulus";
import { DirectUpload } from "@rails/activestorage";
import {
  getMetaValue,
  toArray,
  findElement,
  removeElement,
  insertAfter
} from "helpers";

const MAX_VIDEO_DURATION = 30;

export default class extends Controller {
  static targets = ["input"];

  connect() {
    Dropzone.autoDiscover = false;
    if (this.hasInputTarget) {
      console.log("Dropzone controller initialized")
      this.dropZone = createDropZone(this);
      this.hideFileInput();
      this.bindEvents();
    } else {
      console.warn("Dropzone input target not found. Input is not added to the DOM before initializing the controller.");
    }
  }

  // Private
  hideFileInput() {
    if (this.hasInputTarget) {
      this.inputTarget.disabled = true;
      this.inputTarget.style.display = "none";
    }
  }

  bindEvents() {
    this.dropZone.on("addedfile", file => {

      if (file.size > this.maxFileSize * 1024 * 1024) { // Convert MB to Bytes
        // If the file exceeds the size, it will be automatically removed and not sent to the server. 
        this.dropZone.removeFile(file);
        alert(`File exceeds the maximum size of ${this.maxFileSize}MB.`);
        return;
      }
      
      // Only process if the file is a video
      if (file.type.startsWith('video/')) {
        this.checkVideoDuration(file, MAX_VIDEO_DURATION).then(isValid => {
          if (isValid) {
            setTimeout(() => {
              file.accepted && createDirectUploadController(this, file).start();
            }, 500);
          } else {
            // Remove file if duration exceeds 30 seconds
            // If the file exceeds the time limit, it will be automatically removed and not sent to the server. 
            this.dropZone.removeFile(file);
            alert('Video exceeds the maximum allowed duration of 30 seconds.');
          }
        });
      } else {
        setTimeout(() => {
          file.accepted && createDirectUploadController(this, file).start();
        }, 500);
      }
    });
  
    this.dropZone.on("removedfile", file => {
      file.controller && removeElement(file.controller.hiddenInput);
    });
  
    this.dropZone.on("canceled", file => {
      file.controller && file.controller.xhr.abort();
    });
  }
  
  // Method to check video duration
  checkVideoDuration(file, maxDurationInSeconds) {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const fileURL = URL.createObjectURL(file);
  
      video.preload = 'metadata';
  
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(fileURL); // Free up memory
        const duration = video.duration;
        if (duration <= maxDurationInSeconds) {
          resolve(true);  // Video is valid
        } else {
          resolve(false); // Video exceeds max duration
        }
      };
  
      video.onerror = () => {
        reject(new Error('Error loading video file'));
      };
  
      video.src = fileURL;
    });
  }  

  get headers() {
    return { "X-CSRF-Token": getMetaValue("csrf-token") };
  }

  get url() {
    return this.hasInputTarget ? this.inputTarget.getAttribute("data-direct-upload-url") : null;
  }

  get maxFiles() {
    return this.data.get("maxFiles") || 1;
  }

  get maxFileSize() {
    return this.data.get("maxFileSize") || 256;
  }

  get acceptedFiles() {
    return this.data.get("acceptedFiles");
  }

  get addRemoveLinks() {
    return this.data.get("addRemoveLinks") || true;
  }

  get successClickOn() {
    return this.data.get("successClickOn");
  }
}

class DirectUploadController {
  constructor(source, file) {
    this.directUpload = createDirectUpload(file, source.url, this);
    this.source = source;
    this.file = file;
  }

  start() {
    this.file.controller = this;
    this.hiddenInput = this.createHiddenInput();
    this.directUpload.create((error, attributes) => {
      if (error) {
        removeElement(this.hiddenInput);
        this.emitDropzoneError(error);
      } else {
        this.hiddenInput.value = attributes.signed_id;
        this.emitDropzoneSuccess();
      }
    });
  }

  createHiddenInput() {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = this.source.inputTarget.name;
    insertAfter(input, this.source.inputTarget);
    return input;
  }

  directUploadWillStoreFileWithXHR(xhr) {
    this.bindProgressEvent(xhr);
    this.emitDropzoneUploading();
  }

  bindProgressEvent(xhr) {
    this.xhr = xhr;
    this.xhr.upload.addEventListener("progress", event =>
      this.uploadRequestDidProgress(event)
    );
  }

  uploadRequestDidProgress(event) {
    const element = this.source.element;
    const progress = (event.loaded / event.total) * 100;
    findElement(
      this.file.previewTemplate,
      ".dz-upload"
    ).style.width = `${progress}%`;
  }

  emitDropzoneUploading() {
    this.file.status = Dropzone.UPLOADING;
    this.source.dropZone.emit("processing", this.file);
  }

  emitDropzoneError(error) {
    this.file.status = Dropzone.ERROR;
    this.source.dropZone.emit("error", this.file, error);
    this.source.dropZone.emit("complete", this.file);
  }

  emitDropzoneSuccess() {
    this.file.status = Dropzone.SUCCESS;
    this.source.dropZone.emit("success", this.file);
    this.source.dropZone.emit("complete", this.file);
    if (this.source.successClickOn) {
      console.log("dropzone successClickOn");
      if (document.querySelector(this.source.successClickOn)) {
        console.log("dropzone successClickOn exists");
        document.querySelector(this.source.successClickOn).click();
      }
    }
  }
}

function createDirectUploadController(source, file) {
  return new DirectUploadController(source, file);
}

function createDirectUpload(file, url, controller) {
  return new DirectUpload(file, url, controller);
}

function createDropZone(controller) {
  return new Dropzone(controller.element, {
    url: controller.url,
    headers: controller.headers,
    maxFiles: controller.maxFiles,
    maxFilesize: controller.maxFileSize,
    acceptedFiles: controller.acceptedFiles,
    addRemoveLinks: controller.addRemoveLinks,
    autoQueue: false
  });
}
